/**
 * Pixel-art city landmarks. Each city gets ONE big recognizable monument
 * rendered as part of the scenery — Eiffel Tower in Paris, Pyramid in Cairo,
 * Big Ben in London, etc.
 *
 * Every landmark is drawn with chunky 4-8px "pixels" for that Sega Genesis /
 * Speed Racer look. All shapes are rectangles aligned to a pixel grid.
 *
 * Each drawX() function takes (ctx, x, y) where (x,y) is the BOTTOM-CENTER of
 * the landmark. Sizes are in world units.
 */

// Helper: a single chunky "pixel" block
function px(ctx, x, y, w, h, color) {
  ctx.fillStyle = color;
  ctx.fillRect(Math.floor(x), Math.floor(y), Math.ceil(w), Math.ceil(h));
}

// ======================= EIFFEL TOWER (Paris) =======================
export function drawEiffelTower(ctx, cx, by) {
  const P = "#8B5A2B"; // bronze-brown
  const D = "#6B4423"; // darker shadow
  const W = 120;       // base width
  const H = 220;       // total height

  // Base legs (two trapezoidal feet)
  px(ctx, cx - W/2, by - 28, W, 8, D);
  // Outer leg outlines (pixelated stair-step taper)
  for (let i = 0; i < 7; i++) {
    const y = by - 28 - i*12;
    const inset = i * 5;
    px(ctx, cx - W/2 + inset, y, 8, 12, P);
    px(ctx, cx + W/2 - inset - 8, y, 8, 12, P);
  }
  // First platform (wide horizontal bar)
  px(ctx, cx - 50, by - 112, 100, 6, P);
  px(ctx, cx - 50, by - 118, 100, 6, D);
  // Second stage legs (narrower)
  for (let i = 0; i < 6; i++) {
    const y = by - 118 - i*10;
    const inset = 10 + i * 3;
    px(ctx, cx - 30 + (inset-10), y, 6, 10, P);
    px(ctx, cx + 30 - (inset-10) - 6, y, 6, 10, P);
  }
  // Second platform
  px(ctx, cx - 22, by - 178, 44, 5, P);
  px(ctx, cx - 22, by - 183, 44, 5, D);
  // Upper shaft
  px(ctx, cx - 6, by - 183, 12, 25, P);
  // Top spire
  px(ctx, cx - 3, by - 208, 6, 12, P);
  px(ctx, cx - 1, by - H, 2, 8, "#FFCC00"); // antenna tip
  // X-cross pattern in base section
  ctx.strokeStyle = D;
  ctx.lineWidth = 1;
  for (let i = 0; i < 5; i++) {
    const y = by - 36 - i*14;
    ctx.beginPath();
    ctx.moveTo(cx - 50, y);
    ctx.lineTo(cx + 50, y);
    ctx.stroke();
  }
  // Lattice diagonals
  ctx.strokeStyle = D;
  ctx.beginPath();
  ctx.moveTo(cx - 46, by - 30); ctx.lineTo(cx - 20, by - 106);
  ctx.moveTo(cx + 46, by - 30); ctx.lineTo(cx + 20, by - 106);
  ctx.stroke();
  // shadow puddle
  ctx.fillStyle = "rgba(0,0,0,0.25)";
  ctx.beginPath();
  ctx.ellipse(cx, by - 4, 70, 10, 0, 0, Math.PI * 2);
  ctx.fill();
}

// ======================= PYRAMID (Cairo) =======================
export function drawPyramid(ctx, cx, by) {
  const ROWS = 14;       // stairstep height
  const BLOCK = 10;      // pixel block size
  // Stair-step silhouette
  for (let r = 0; r < ROWS; r++) {
    const w = (ROWS - r) * BLOCK * 2;
    const y = by - (r + 1) * BLOCK;
    // sunlit left face
    px(ctx, cx - w/2, y, w/2, BLOCK, r % 2 === 0 ? "#E8C579" : "#DDB463");
    // shaded right face
    px(ctx, cx, y, w/2, BLOCK, r % 2 === 0 ? "#A67C3E" : "#8B6830");
  }
  // Sandy base
  px(ctx, cx - ROWS * BLOCK - 10, by, ROWS * BLOCK * 2 + 20, 6, "#CCA96A");
  // Small hieroglyph detail (black dots)
  for (let i = 0; i < 3; i++) {
    px(ctx, cx - 10 + i*8, by - 40, 3, 3, "#000");
  }
}

// ======================= BIG BEN (London) =======================
export function drawBigBen(ctx, cx, by) {
  const W = 44;
  const stoneL = "#C9B99A";
  const stoneD = "#8A7B5F";
  const roof = "#4B5D3A";
  // Base up the clock
  for (let i = 0; i < 14; i++) {
    const y = by - 10 - i*12;
    px(ctx, cx - W/2, y, W, 12, i % 2 === 0 ? stoneL : stoneD);
    // window slits
    px(ctx, cx - 4, y + 3, 3, 6, "#1a1a1a");
  }
  // Clock face
  const clockY = by - 178;
  px(ctx, cx - 18, clockY, 36, 30, "#FFE8A7");
  px(ctx, cx - 18, clockY, 36, 4, stoneD);
  // Clock hands
  ctx.strokeStyle = "#1a1a1a";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(cx, clockY + 15); ctx.lineTo(cx, clockY + 4);
  ctx.moveTo(cx, clockY + 15); ctx.lineTo(cx + 9, clockY + 15);
  ctx.stroke();
  // Tower peak
  px(ctx, cx - W/2 + 4, by - 208, W - 8, 14, roof);
  // Spire
  px(ctx, cx - 3, by - 232, 6, 24, roof);
  px(ctx, cx - 1, by - 240, 2, 8, "#FFD700");
  // Shadow
  ctx.fillStyle = "rgba(0,0,0,0.25)";
  ctx.beginPath();
  ctx.ellipse(cx, by - 4, 34, 7, 0, 0, Math.PI * 2);
  ctx.fill();
}

// ======================= STATUE OF LIBERTY (NYC) =======================
export function drawStatueOfLiberty(ctx, cx, by) {
  const teal = "#57B8A0";
  const tealD = "#3F8A77";
  // Pedestal
  for (let i = 0; i < 6; i++) {
    px(ctx, cx - 24, by - 8 - i*8, 48, 8, i % 2 === 0 ? "#7A6E52" : "#5C533E");
  }
  // Body (robe)
  px(ctx, cx - 14, by - 80, 28, 60, teal);
  px(ctx, cx - 14, by - 80, 4, 60, tealD);
  // Left arm with tablet (down)
  px(ctx, cx - 20, by - 68, 8, 16, teal);
  px(ctx, cx - 24, by - 52, 14, 10, "#DEB887"); // tablet
  // Right arm raised with torch
  px(ctx, cx + 8, by - 100, 6, 20, teal);
  px(ctx, cx + 4, by - 116, 14, 6, "#B8860B"); // torch handle
  // Flames
  px(ctx, cx + 6, by - 128, 4, 12, "#FFD700");
  px(ctx, cx + 10, by - 132, 3, 16, "#FF8C00");
  px(ctx, cx + 4, by - 124, 3, 8, "#FFA500");
  // Head
  px(ctx, cx - 7, by - 96, 14, 14, teal);
  // Crown spikes
  for (let i = 0; i < 7; i++) {
    px(ctx, cx - 10 + i*3, by - 104, 2, 6, teal);
  }
  // Face
  px(ctx, cx - 2, by - 90, 2, 2, "#000");
  px(ctx, cx + 2, by - 90, 2, 2, "#000");
  // Shadow
  ctx.fillStyle = "rgba(0,0,0,0.3)";
  ctx.beginPath();
  ctx.ellipse(cx, by - 4, 30, 6, 0, 0, Math.PI * 2);
  ctx.fill();
}

// ======================= CN TOWER (Toronto) =======================
export function drawCNTower(ctx, cx, by) {
  const white = "#F4F4F4";
  const grey = "#8B8B8B";
  // Narrow shaft
  px(ctx, cx - 5, by - 200, 10, 200, white);
  px(ctx, cx - 5, by - 200, 2, 200, grey);
  // Observation pod (saucer)
  px(ctx, cx - 22, by - 150, 44, 16, white);
  px(ctx, cx - 22, by - 150, 44, 4, grey);
  px(ctx, cx - 24, by - 142, 48, 4, "#1976D2"); // blue windows band
  // Lower level
  px(ctx, cx - 18, by - 130, 36, 6, white);
  // Top spire
  px(ctx, cx - 3, by - 240, 6, 40, white);
  px(ctx, cx - 1, by - 250, 2, 10, "#E31C1C"); // antenna red
  // Base
  px(ctx, cx - 12, by - 10, 24, 10, "#666");
  // Shadow
  ctx.fillStyle = "rgba(0,0,0,0.25)";
  ctx.beginPath();
  ctx.ellipse(cx, by - 4, 26, 5, 0, 0, Math.PI * 2);
  ctx.fill();
}

// ======================= HOLLYWOOD SIGN (LA) =======================
export function drawHollywoodSign(ctx, cx, by) {
  const letters = "HOLLYWOOD";
  const w = 14;
  const h = 32;
  const gap = 2;
  const totalW = letters.length * (w + gap) - gap;
  let x = cx - totalW / 2;
  // Hill under it
  ctx.fillStyle = "#8D6C3E";
  ctx.beginPath();
  ctx.moveTo(cx - 120, by);
  ctx.lineTo(cx - 80, by - 18);
  ctx.lineTo(cx + 80, by - 22);
  ctx.lineTo(cx + 120, by);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = "#5D4724";
  ctx.lineWidth = 2;
  ctx.stroke();
  for (let i = 0; i < letters.length; i++) {
    // Support scaffolding
    px(ctx, x + 2, by - 18, 2, 20, "#3a3a3a");
    px(ctx, x + w - 4, by - 18, 2, 20, "#3a3a3a");
    // White letter block
    px(ctx, x, by - 18 - h, w, h, "#F8F8F8");
    // Letter outline
    ctx.strokeStyle = "#1a1a1a";
    ctx.lineWidth = 2;
    ctx.strokeRect(x, by - 18 - h, w, h);
    // Actual letter
    ctx.fillStyle = "#1a1a1a";
    ctx.font = "bold 16px 'Press Start 2P', monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(letters[i], x + w / 2, by - 18 - h / 2);
    x += w + gap;
  }
}

// ======================= SHIBUYA CROSSING (Tokyo) =======================
export function drawShibuyaScreen(ctx, cx, by) {
  // Three neon skyscrapers with animated billboards
  const t = performance.now() / 1000;
  const floors = 10;
  // Left building
  px(ctx, cx - 60, by - 160, 40, 160, "#222");
  // Billboard with pulsing pink
  const p = (Math.sin(t * 1.5) + 1) / 2;
  px(ctx, cx - 58, by - 158, 36, 46, `rgb(${200 + p*55}, ${20 + p*40}, ${120})`);
  // Vertical kanji stripes
  for (let i = 0; i < 3; i++) {
    px(ctx, cx - 52 + i*10, by - 154, 2, 38, "#FFF");
  }
  // Middle (tallest) building
  px(ctx, cx - 15, by - 220, 30, 220, "#1a1a2e");
  // Neon sign
  px(ctx, cx - 13, by - 218, 26, 40, "#0ff");
  px(ctx, cx - 13, by - 174, 26, 30, `rgb(${60 + p*100}, ${0 + p*50}, ${200})`);
  for (let f = 0; f < floors; f++) {
    for (let c = 0; c < 4; c++) {
      if (((f * 7 + c * 13) % 4) < 3) {
        px(ctx, cx - 13 + c*7 + 1, by - 140 + f*14, 3, 8, "#FFD23F");
      }
    }
  }
  // Right building
  px(ctx, cx + 20, by - 140, 40, 140, "#1e1e1e");
  px(ctx, cx + 22, by - 138, 36, 40, "#ff0088");
  // animated scrolling window strip
  for (let f = 0; f < 6; f++) {
    const col = (Math.sin(t + f) + 1) / 2;
    px(ctx, cx + 22, by - 96 + f*12, 36, 3, `rgb(${col*255}, ${100 + col*155}, 255)`);
  }
  // Radio tower silhouette behind
  px(ctx, cx - 1, by - 280, 2, 60, "#E31C1C");
  px(ctx, cx - 8, by - 240, 16, 4, "#E31C1C");
  px(ctx, cx - 14, by - 220, 28, 8, "#E31C1C");
}

// ======================= CEDAR OF LEBANON (Beirut) =======================
export function drawCedar(ctx, cx, by) {
  // Classic cedar silhouette — 3 flat canopy tiers on a sturdy trunk.
  const dark = "#2E5D3A";
  const mid = "#3D7A4B";
  const hi = "#4F9C5F";
  // Trunk
  px(ctx, cx - 6, by - 70, 12, 70, "#5A4024");
  px(ctx, cx - 6, by - 70, 3, 70, "#3E2B18");
  // Bottom canopy (widest)
  px(ctx, cx - 70, by - 90, 140, 20, dark);
  px(ctx, cx - 60, by - 96, 120, 10, mid);
  // Middle canopy
  px(ctx, cx - 55, by - 120, 110, 18, mid);
  px(ctx, cx - 45, by - 126, 90, 10, hi);
  // Top canopy
  px(ctx, cx - 40, by - 148, 80, 20, dark);
  px(ctx, cx - 30, by - 154, 60, 10, mid);
  // Crown shine
  px(ctx, cx - 15, by - 162, 30, 8, hi);
  // Scattered little highlights
  for (let i = 0; i < 8; i++) {
    const rx = cx - 60 + i * 15;
    px(ctx, rx, by - 88 + (i % 3) * 3, 3, 3, hi);
  }
}

// ======================= MEXICO AZTEC PYRAMID (Mexico City) =======================
export function drawAztecPyramid(ctx, cx, by) {
  const stone1 = "#D4A574";
  const stone2 = "#A87D4E";
  const stone3 = "#7D5A30";
  const ROWS = 5;
  const BLOCK = 18;
  for (let r = 0; r < ROWS; r++) {
    const w = (ROWS - r) * 40 + 40;
    const y = by - (r + 1) * BLOCK;
    px(ctx, cx - w/2, y, w, BLOCK, r % 2 === 0 ? stone1 : stone2);
    // darker line separator
    px(ctx, cx - w/2, y, w, 3, stone3);
  }
  // Central stair
  for (let r = 0; r < ROWS; r++) {
    const y = by - (r + 1) * BLOCK;
    px(ctx, cx - 8, y, 16, 3, stone3);
  }
  // Top temple
  px(ctx, cx - 20, by - ROWS * BLOCK - 26, 40, 26, "#E63946");
  px(ctx, cx - 16, by - ROWS * BLOCK - 20, 32, 4, "#FFCC00");
  px(ctx, cx - 6, by - ROWS * BLOCK - 16, 12, 16, "#1a1a1a");
}

// ======================= RENAISSANCE CENTER (Detroit) =======================
export function drawRenCen(ctx, cx, by) {
  // Cluster of 5 glass towers, center one tallest (like Detroit GM RenCen).
  const heights = [140, 170, 220, 170, 140];
  const widths = [22, 26, 34, 26, 22];
  const sp = 4;
  let x = cx - (widths.reduce((a, b) => a + b, 0) + sp * 4) / 2;
  const blueL = "#6FA3D0";
  const blueD = "#3C678C";
  for (let i = 0; i < heights.length; i++) {
    const w = widths[i], h = heights[i];
    px(ctx, x, by - h, w, h, blueL);
    px(ctx, x, by - h, 4, h, blueD); // left shadow strip
    // Window grid
    for (let r = 0; r < h / 8; r++) {
      for (let c = 0; c < w / 4 - 1; c++) {
        if (((r + c + i) % 3) === 0) {
          px(ctx, x + 2 + c*4, by - h + 4 + r*8, 2, 3, "#FFE08F");
        }
      }
    }
    // Cap
    px(ctx, x, by - h, w, 3, "#1a1a1a");
    x += w + sp;
  }
  // Shadow
  ctx.fillStyle = "rgba(0,0,0,0.3)";
  ctx.beginPath();
  ctx.ellipse(cx, by - 4, 90, 8, 0, 0, Math.PI * 2);
  ctx.fill();
}

// Dispatcher: given a city_id, draw its landmark at the given world position.
const LANDMARK_DRAWERS = {
  paris: drawEiffelTower,
  cairo: drawPyramid,
  london: drawBigBen,
  nyc: drawStatueOfLiberty,
  toronto: drawCNTower,
  la: drawHollywoodSign,
  tokyo: drawShibuyaScreen,
  beirut: drawCedar,
  mexico_city: drawAztecPyramid,
  detroit: drawRenCen,
};

// Each city's landmark anchor (bottom-center, in world coords).
// Placed so the landmark sits on the grass area not covered by the road track.
// Most tracks leave the top-center and bottom-corners open — landmarks go
// wherever makes the city most recognizable.
export const LANDMARK_POSITIONS = {
  paris: { x: 640, y: 320 },        // figure-8 center (Eiffel between loops)
  cairo: { x: 1080, y: 230 },       // upper-right of serpentine
  london: { x: 640, y: 390 },       // grid intersection
  nyc: { x: 1080, y: 260 },         // right-side (skyline cluster)
  toronto: { x: 1080, y: 270 },     // right side grid
  la: { x: 640, y: 260 },           // centered in speedway upper band
  tokyo: { x: 640, y: 260 },        // speedway centerpiece
  beirut: { x: 640, y: 260 },       // inside oval
  mexico_city: { x: 640, y: 260 },  // inside oval
  detroit: { x: 640, y: 260 },      // centered in speedway
};

export function drawCityLandmark(ctx, cityId) {
  const drawer = LANDMARK_DRAWERS[cityId];
  const pos = LANDMARK_POSITIONS[cityId];
  if (!drawer || !pos) return;
  ctx.save();
  // Upscale landmarks so they're readable centerpieces.
  const SCALE = cityId === "tokyo" ? 0.95 : 1.25;
  ctx.translate(pos.x, pos.y);
  ctx.scale(SCALE, SCALE);
  drawer(ctx, 0, 0);
  ctx.restore();
}
